import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {enqueueCommand} from '@/entities/device/api';
import {
  addStreamCamera,
  deleteStreamSource,
  fetchStreamCameras,
  rotateCameraSecret,
} from '@/entities/stream/api';
import type {CameraCredentials} from '@/entities/stream/types';
import {CameraPlayer} from './CameraPlayer';
import {matchesTerminal} from '../lib/terminal-match';
import {messageOf} from '@/shared/lib/errors';
import {Badge} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * EXPERIMENT (branch `experiment/terminal-live-video`): live video from a terminal's
 * own camera, inside its card.
 *
 * The terminal streams as a camera "added by hand" (firmware `live_video.rs`). One tap
 * here does the whole setup with no one touching the terminal:
 *
 *   POST /stream/cameras {name: <terminal name>}          → sourceId, secret, signalingUrl
 *   POST /control/devices/{id}/commands live_video_enable → the terminal picks it up on
 *                                                            its next sync (≤ 30 s)
 *
 * The camera carries the terminal's name, and the name is how this card finds it again —
 * a manual camera gets its own `cam-…` id. A future native source would have `peer` =
 * `SVFID-…` (cameras/source-contract.md §1); that and the device id are accepted too.
 *
 * Firmware without the live-video build answers the command with "unknown command
 * kind" — the card then keeps saying the terminal is not streaming.
 */
/** How long the card keeps polling after sending the command: two sync rounds. */
const WAIT_MS = 75_000;

const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  title: {flex: 1},
  actions: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
});

export function TerminalLiveVideo({
  companyId,
  deviceId,
  machineNumber,
  deviceName,
}: {
  companyId: string;
  deviceId: string;
  machineNumber?: string | null;
  deviceName?: string;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const ids = [machineNumber, deviceId].filter((v): v is string => !!v);
  const [waitUntil, setWaitUntil] = useState(0);
  const [waiting, setWaiting] = useState(false);
  // Ends the wait on time even if no refetch changes anything (structural sharing
  // would otherwise leave the card polling and "waiting" forever).
  useEffect(() => {
    const left = waitUntil - Date.now();
    setWaiting(left > 0);
    if (left <= 0) {
      return;
    }
    const timer = setTimeout(() => setWaiting(false), left);
    return () => clearTimeout(timer);
  }, [waitUntil]);

  const query = useQuery({
    queryKey: ['stream-cameras', companyId],
    queryFn: () => fetchStreamCameras(companyId),
    refetchInterval: waiting ? 5_000 : false,
  });

  const cameras = (query.data?.data ?? []).filter(c => matchesTerminal(c, ids, deviceName));
  const camera = cameras[0];
  const online = camera?.online === true;

  const send = (creds: CameraCredentials) =>
    enqueueCommand(companyId, deviceId, 'live_video_enable', {
      signalingUrl: creds.signalingUrl,
      secret: creds.secret,
    });

  // No camera yet → create one under the terminal's name. A camera that exists but is
  // not online → its secret was lost with the terminal (reflash, swap): mint a new one.
  const enable = useMutation({
    mutationFn: async () => {
      const creds = camera
        ? await rotateCameraSecret(companyId, camera.peer)
        : await addStreamCamera(companyId, deviceName || machineNumber || deviceId);
      await send(creds);
    },
    onSuccess: () => {
      setWaitUntil(Date.now() + WAIT_MS);
      void qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
    },
  });

  const disable = useMutation({
    mutationFn: async () => {
      await enqueueCommand(companyId, deviceId, 'live_video_disable');
      if (camera) {
        await deleteStreamSource(companyId, camera.peer);
      }
    },
    onSuccess: () => {
      setWaitUntil(0);
      void qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
    },
  });

  const busy = enable.isPending || disable.isPending;
  const error = enable.error ?? disable.error;

  return (
    <Card padding={4} style={styles.root}>
      <View style={styles.head}>
        <AppText variant="label" style={styles.title}>
          {t('live.terminal.title')}
        </AppText>
        <Badge variant="warn" size="sm" label={t('live.terminal.experimental')} />
      </View>

      {query.isPending ? (
        <Skeleton height={160} radius={radii.lg} />
      ) : query.isError ? (
        <Banner tone="error" title={t('live.terminal.loadFailed')} />
      ) : camera && online ? (
        cameras.map(c => (
          <CameraPlayer key={c.deviceId} companyId={companyId} camera={c} />
        ))
      ) : waiting ? (
        <Banner
          tone="info"
          title={t('live.terminal.waiting')}
          message={t('live.terminal.waitingHint')}
        />
      ) : (
        <Banner
          tone="info"
          title={t('live.terminal.notStreaming')}
          message={t('live.terminal.notStreamingHint')}
        />
      )}

      {error ? <Banner tone="error" title={messageOf(error, t('errors.unknown'))} /> : null}

      <View style={styles.actions}>
        {!online && !waiting ? (
          <AppButton
            title={t(camera ? 'live.terminal.reconnect' : 'live.terminal.enable')}
            tone="signal"
            icon="play"
            size="sm"
            loading={enable.isPending}
            disabled={busy}
            onPress={() => enable.mutate()}
          />
        ) : null}
        {camera ? (
          <AppButton
            title={t('live.terminal.disable')}
            tone="neutral"
            size="sm"
            loading={disable.isPending}
            disabled={busy}
            onPress={() => disable.mutate()}
          />
        ) : null}
      </View>
    </Card>
  );
}
